import {
  getCustomerReturn,
  getQuotation,
  getSalesOrder,
  getSalesReps,
  setCustomerReturns,
  setCustomerReturnSelection,
  setQuotations,
  setQuotationSelection,
  setSalesOrders,
  setSalesOrderSelection,
} from "../slice/salesSlice";
import { apiSlice } from "./apiSlice";
import { fileResponseHandler } from "./utils";

export const salesApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    getSalesRepList: builder.query({
      query: params => ({
        url: "/api/user/v1/sales_rep/",
        params,
      }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(getSalesReps(result.data))).catch(error => {});
      },
      providesTags: ["salesRep"],
    }),
    getPendingSalesRepInvitations: builder.query({
      query: () => "/api/user/v1/sales_rep/pending_invitations/",
      providesTags: ["salesRep"],
    }),
    getSalesRepFilterOptions: builder.query({
      query: field => ({
        url: "/api/user/v1/sales_rep/filter_options/",
        params: field ? { field } : undefined,
      }),
      providesTags: ["salesRep"],
    }),
    getSalesRepSelection: builder.query({
      query: params => ({
        url: "/api/user/v1/sales_rep/selection/",
        params,
      }),
      providesTags: ["salesRep"],
    }),
    getSalesRepById: builder.query({
      query: id => `/api/user/v1/sales_rep/${id}/`,
      providesTags: (result, error, id) => [{ type: "salesRep", id }],
    }),
    getSalesRepInvitation: builder.query({
      query: id => `/api/user/v1/sales_rep/invitation/${id}/`,
      providesTags: (result, error, id) => [{ type: "salesRep", id: `invitation:${id}` }],
    }),
    createSalesRep: builder.mutation({
      query: body => ({
        url: "/api/user/v1/sales_rep/",
        method: "POST",
        body,
      }),
      invalidatesTags: ["salesRep"],
    }),
    updateSalesRep: builder.mutation({
      query: ({ id, ...rest }) => ({
        url: `/api/user/v1/sales_rep/${id}/`,
        method: "PATCH",
        body: rest,
      }),
      invalidatesTags: ["salesRep"],
    }),
    updateSalesRepInvitation: builder.mutation({
      query: ({ id, ...rest }) => ({
        url: `/api/user/v1/sales_rep/invitation/${id}/`,
        method: "PATCH",
        body: rest,
      }),
      invalidatesTags: ["salesRep"],
    }),
    deleteSalesRep: builder.mutation({
      query: id => ({
        url: `/api/user/v1/sales_rep/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["salesRep"],
    }),
    createQuotation: builder.mutation({
      query: data => ({
        url: "/api/sale/v1/quotation/",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["quotation"],
    }),
    updateQuotation: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/sale/v1/quotation/${id}/`,
        method: "PUT",
        body: data,
      }),
    }),
    patchQuotation: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/sale/v1/quotation/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "quotationDetail", id },
        { type: "quotationLines", id },
        { type: "quotationOptionalLines", id },
        "quotation",
      ],
    }),
    getQuotationDetail: builder.query({
      query: id => `/api/sale/v1/quotation/${id}/`,
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(getQuotation(result.data))).catch(error => {});
      },
      providesTags: (result, error, id) => [{ type: "quotationDetail", id }],
    }),
    getQuotationList: builder.query({
      query: params => ({
        url: "/api/sale/v1/quotation/",
        params,
      }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(setQuotations(result.data))).catch(error => {});
      },
      providesTags: ["quotation"],
    }),

    getQuotationSelection: builder.query({
      query: () => "/api/sale/v1/quotation/selection/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(setQuotationSelection(result.data)))
          .catch(error => {});
      },
    }),
    getQuotationLines: builder.query({
      query: params => ({
        url: "/api/sale/v1/quotation_line/",
        params,
      }),
      providesTags: (result, error, params) => [
        { type: "quotationLines", id: params?.quotation_ids },
      ],
    }),
    getOptionalQuotationLines: builder.query({
      query: params => ({
        url: "/api/sale/v1/optional_quotation_line/",
        params,
      }),
      providesTags: (result, error, params) => [
        { type: "quotationOptionalLines", id: params?.quotation_ids },
      ],
    }),
    cancelQuotation: builder.mutation({
      query: ({ id, reason }) => ({
        url: `/api/sale/v1/quotation/${id}/cancel/`,
        method: "POST",
        body: { reason },
      }),
      invalidatesTags: ["quotation"],
    }),
    sendQuotation: builder.mutation({
      query: id => ({
        url: `/api/sale/v1/quotation/${id}/send/`,
        method: "POST",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "quotationDetail", id },
        { type: "quotationLines", id },
        { type: "quotationOptionalLines", id },
        "quotation",
      ],
    }),
    convertQuotationToSaleOrder: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/sale/v1/quotation/${id}/convert_to_sale_order/`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "quotationDetail", id },
        { type: "quotationLines", id },
        { type: "quotationOptionalLines", id },
        "quotation",
      ],
    }),
    applySalesOrderPromo: builder.mutation({
      query: ({ id, code, promo_id }) => ({
        url: `/api/sale/v1/sale_order/${id}/apply-promo/`,
        method: "POST",
        body: { code, promo_id },
      }),
      invalidatesTags: (result, error, { id }) => ["order", { type: "orderDetail", id }],
    }),
    removeSalesOrderPromo: builder.mutation({
      query: ({ id }) => ({
        url: `/api/sale/v1/sale_order/${id}/remove-promo/`,
        method: "POST",
        body: {},
      }),
      invalidatesTags: (result, error, { id }) => ["order", { type: "orderDetail", id }],
    }),
    applyQuotationPromo: builder.mutation({
      query: ({ id, code, promo_id }) => ({
        url: `/api/sale/v1/quotation/${id}/apply-promo/`,
        method: "POST",
        body: { code, promo_id },
      }),
      invalidatesTags: (result, error, { id }) => ["quotation", { type: "quotationDetail", id }],
    }),
    removeQuotationPromo: builder.mutation({
      query: ({ id }) => ({
        url: `/api/sale/v1/quotation/${id}/remove-promo/`,
        method: "POST",
        body: {},
      }),
      invalidatesTags: (result, error, { id }) => ["quotation", { type: "quotationDetail", id }],
    }),
    createSalesOrder: builder.mutation({
      query: data => ({
        url: "/api/sale/v1/sale_order/",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["order"],
    }),
    updateSalesOrder: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/sale/v1/sale_order/${id}/`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => ["order", { type: "orderDetail", id }],
    }),
    patchSalesOrder: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/sale/v1/sale_order/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => ["order", { type: "orderDetail", id }],
    }),
    deleteSalesOrder: builder.mutation({
      query: id => ({
        url: `/api/sale/v1/sale_order/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["order"],
    }),
    getSalesOrderFulfilmentStatus: builder.query({
      query: id => `/api/sale/v1/sale_order/${id}/fulfilment-status/`,
      providesTags: ["SalesOrderFulfilment"],
    }),
    getSalesOrderDetail: builder.query({
      query: (id, params = null) =>
        params ? `/api/sale/v1/sale_order/${id}/?${params}` : `/api/sale/v1/sale_order/${id}/`,
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(getSalesOrder(result.data))).catch(error => {});
      },
      providesTags: (result, error, id) => [{ type: "orderDetail", id }],
    }),
    getSaleOrderHistory: builder.query({
      query: ({ id, limit = 20, offset = 0 }) => ({
        url: `/api/sale/v1/sale_order/${id}/history/`,
        params: { limit, offset },
      }),
    }),
    getSalesOrderList: builder.query({
      query: params => ({
        url: "/api/sale/v1/sale_order/",
        params,
      }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(setSalesOrders(result.data))).catch(error => {});
      },
      providesTags: ["order"],
    }),
    getSalesOrderSelection: builder.query({
      providesTags: ["order"],
      query: params => ({
        url: "/api/sale/v1/sale_order/selection/",
        params,
      }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(setSalesOrderSelection(result.data)))
          .catch(error => {});
      },
    }),
    getSalesOrderLines: builder.query({
      query: params => ({
        url: "/api/sale/v1/sale_order_line/",
        params: { limit: 1000, ...params },
      }),
      providesTags: ["SalesOrderLines"],
    }),
    getSOServiceLines: builder.query({
      query: params => ({
        url: "/api/sale/v1/sale_order_service_line/",
        params: { limit: 1000, ...params },
      }),
    }),
    getSalesOrderActivities: builder.query({
      query: params => ({
        url: "/api/sale/v1/sale_order_activity/",
        params,
      }),
    }),
    cancelSalesOrder: builder.mutation({
      query: ({ id, reason }) => ({
        url: `/api/sale/v1/sale_order/${id}/cancel/`,
        method: "POST",
        body: { reason },
      }),
      invalidatesTags: ["order"],
    }),
    downloadSaleOrderPdf: builder.query({
      query: arg => {
        const id = arg && typeof arg === "object" ? arg.id : arg;
        const layoutId = arg && typeof arg === "object" ? arg.layoutId : null;
        const suffix = layoutId ? `?layout_id=${encodeURIComponent(layoutId)}` : "";
        return {
          url: `/api/sale/v1/sale_order/${id}/download_pdf/${suffix}`,
          responseHandler: fileResponseHandler,
        };
      },
    }),
    downloadSaleOrderExcel: builder.query({
      query: arg => {
        const id = arg && typeof arg === "object" ? arg.id : arg;
        const layoutId = arg && typeof arg === "object" ? arg.layoutId : null;
        const suffix = layoutId ? `?layout_id=${encodeURIComponent(layoutId)}` : "";
        return {
          url: `/api/sale/v1/sale_order/${id}/download_excel/${suffix}`,
          responseHandler: fileResponseHandler,
        };
      },
    }),
    shareSaleOrder: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/sale/v1/sale_order/${id}/share/`,
        method: "POST",
        body: data,
      }),
    }),
    getReturnDetail: builder.query({
      query: id => `/api/sale/v1/return/${id}/`,
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(getCustomerReturn(result.data))).catch(error => {});
      },
      providesTags: (result, error, id) => [{ type: "ReturnDetail", id }],
    }),
    getReturnHistory: builder.query({
      query: ({ id, limit = 20, offset = 0 }) => ({
        url: `/api/sale/v1/return/${id}/history/`,
        params: { limit, offset },
      }),
    }),
    getReturnList: builder.query({
      query: params => ({
        url: "/api/sale/v1/return/",
        params,
      }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(setCustomerReturns(result.data))).catch(error => {});
      },
      providesTags: ["Return"],
    }),
    getReturnSelection: builder.query({
      query: params => ({
        url: "/api/sale/v1/return/selection/",
        params,
      }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(setCustomerReturnSelection(result.data)))
          .catch(error => {});
      },
    }),
    getReturnLines: builder.query({
      query: params => ({
        url: "/api/sale/v1/return_line/",
        params,
      }),
    }),
    getReturnServiceLines: builder.query({
      query: params => ({
        url: "/api/sale/v1/return_service_line/",
        params,
      }),
    }),
    getReturnFeeLines: builder.query({
      query: params => ({
        url: "/api/sale/v1/return_fee/",
        params,
      }),
    }),
    createReturn: builder.mutation({
      query: data => ({
        url: "/api/sale/v1/return/",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Return"],
    }),
    patchReturn: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/sale/v1/return/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => ["Return", { type: "ReturnDetail", id }],
    }),
    deleteReturn: builder.mutation({
      query: id => ({
        url: `/api/sale/v1/return/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["Return"],
    }),
    approveReturn: builder.mutation({
      query: id => ({
        url: `/api/sale/v1/return/${id}/approve/`,
        method: "POST",
      }),
      invalidatesTags: (result, error, id) => ["Return", { type: "ReturnDetail", id }],
    }),
    receiveReturn: builder.mutation({
      query: id => ({
        url: `/api/sale/v1/return/${id}/mark_received/`,
        method: "POST",
      }),
      invalidatesTags: (result, error, id) => ["Return", { type: "ReturnDetail", id }],
    }),
    completeReturn: builder.mutation({
      query: id => ({
        url: `/api/sale/v1/return/${id}/complete/`,
        method: "POST",
      }),
      invalidatesTags: (result, error, id) => ["Return", { type: "ReturnDetail", id }],
    }),
    cancelReturn: builder.mutation({
      query: ({ id, reason }) => ({
        url: `/api/sale/v1/return/${id}/cancel/`,
        method: "POST",
        body: { reason },
      }),
      invalidatesTags: (result, error, { id }) => ["Return", { type: "ReturnDetail", id }],
    }),
    createSaleOrderFiles: builder.mutation({
      query: data => ({
        url: `/api_docdigit/document/create_so_files/`,
        method: "POST",
        body: data,
      }),
    }),
  }),
});

export const {
  useGetSalesRepListQuery,
  useGetPendingSalesRepInvitationsQuery,
  useGetSalesRepFilterOptionsQuery,
  useGetSalesRepSelectionQuery,
  useGetSalesRepByIdQuery,
  useGetSalesRepInvitationQuery,
  useCreateSalesRepMutation,
  useUpdateSalesRepMutation,
  useUpdateSalesRepInvitationMutation,
  useDeleteSalesRepMutation,
  useCreateQuotationMutation,
  useUpdateQuotationMutation,
  usePatchQuotationMutation,
  useGetQuotationListQuery,
  useGetQuotationDetailQuery,
  useLazyGetQuotationDetailQuery,
  useGetQuotationLinesQuery,
  useGetOptionalQuotationLinesQuery,
  useLazyGetQuotationLinesQuery,
  useLazyGetOptionalQuotationLinesQuery,
  useCancelQuotationMutation,
  useSendQuotationMutation,
  useConvertQuotationToSaleOrderMutation,
  useLazyGetQuotationSelectionQuery,
  useApplySalesOrderPromoMutation,
  useRemoveSalesOrderPromoMutation,
  useApplyQuotationPromoMutation,
  useRemoveQuotationPromoMutation,
  useCreateSalesOrderMutation,
  useUpdateSalesOrderMutation,
  usePatchSalesOrderMutation,
  useDeleteSalesOrderMutation,
  useGetSalesOrderDetailQuery,
  useLazyGetSalesOrderDetailQuery,
  useGetSalesOrderFulfilmentStatusQuery,
  useLazyGetSalesOrderFulfilmentStatusQuery,
  useGetSalesOrderListQuery,
  useLazyGetSalesOrderListQuery,
  useGetSalesOrderSelectionQuery,
  useLazyGetSalesOrderSelectionQuery,
  useGetSalesOrderLinesQuery,
  useLazyGetSalesOrderLinesQuery,
  useGetSOServiceLinesQuery,
  useLazyGetSOServiceLinesQuery,
  useGetSalesOrderActivitiesQuery,
  useCancelSalesOrderMutation,
  useLazyDownloadSaleOrderPdfQuery,
  useLazyDownloadSaleOrderExcelQuery,
  useShareSaleOrderMutation,
  useCreateSaleOrderFilesMutation,
  useGetSaleOrderHistoryQuery,
  useGetReturnDetailQuery,
  useLazyGetReturnDetailQuery,
  useGetReturnListQuery,
  useLazyGetReturnListQuery,
  useGetReturnSelectionQuery,
  useLazyGetReturnSelectionQuery,
  useGetReturnLinesQuery,
  useLazyGetReturnLinesQuery,
  useGetReturnServiceLinesQuery,
  useLazyGetReturnServiceLinesQuery,
  useGetReturnFeeLinesQuery,
  useLazyGetReturnFeeLinesQuery,
  useGetReturnHistoryQuery,
  useCreateReturnMutation,
  usePatchReturnMutation,
  useDeleteReturnMutation,
  useApproveReturnMutation,
  useReceiveReturnMutation,
  useCompleteReturnMutation,
  useCancelReturnMutation,
} = salesApiSlice;
